<template>
  <div>
    <section-header size="xl" variants="dividing">
      <heading level="1" size="xl" v-html="bp('textPageTitle')" />
      <div class="buttons buttons--right-for-md">
        <trek-button primary small icon="get_app" @click="downloadOrderDetails">
          <span v-html="bp('textDownloadOrder')" />
        </trek-button>
      </div>
    </section-header>

    <div v-if="!loading" class="grid-x grid-margin-x">
      <div class="cell medium-6 large-4 small-order-2 medium-order-1">
        <account-order-details-info :order-data="orderData" :base-properties="baseProperties" />
      </div>
      <div v-if="!consumerFriendlyMode" class="cell medium-6 large-4 large-offset-4 small-order-1 medium-order-2">
        <account-order-details-summary :order-data="orderData" :base-properties="baseProperties" />
      </div>
    </div>

    <tabs id="order-tabs" class="mt-5 text-base">
      <template v-for="tab in tabs">
        <tab-pane v-if="orderData[tab.key]" :key="tab.id">
          <div slot="label">
            <h3 class="heading heading--md" v-html="tab.label" />
          </div>

          <collapse
            ref="collapse"
            icon-active="indeterminate_check_box"
            icon-inactive="add_box"
            icon-position="right"
            :default-active="true"
          >
            <template v-for="shipment in Object.keys(orderData[tab.key])">
              <collapse-item
                v-if="shipment != 'NOT_SHIPPED' && shipment != 'BACKORDERED' && shipment != 'ALLOCATED'"
                :key="shipment"
                :name="shipment"
              >
                <div slot="title" class="heading heading--sm" v-html="shipment" />
                <account-order-details-shipment
                  :shipment="orderData[tab.key][shipment]"
                  :order-data="orderData"
                  :product-type="tab.key"
                  :table-data="orderData[tab.key][shipment]"
                  status="shipped"
                  :base-properties="baseProperties"
                  :table-headers="tableHeaders"
                >
                  <div>
                    <account-order-details-shipment-info
                      :base-properties="baseProperties"
                      :shipment="orderData[tab.key][shipment]"
                      :name="shipment"
                      :is-credit-memo="orderData.details.isCreditMemo"
                      :order-data="orderData"
                    />
                    <account-order-details-shipment-grid
                      :base-properties="baseProperties"
                      :order-data="orderData"
                      :table-headers="tableHeaders"
                      :table-data="orderData[tab.key][shipment]"
                      :shipment="shipment"
                    />
                  </div>
                </account-order-details-shipment>
              </collapse-item>
            </template>

            <collapse-item v-if="orderData[tab.key].NOT_SHIPPED" name="NOT_SHIPPED">
              <div slot="title" class="heading heading--sm" v-html="bp('notShipped')" />
              <account-order-details-shipment
                :order-data="orderData"
                :table-headers="tableHeaders"
                :table-data="orderData[tab.key].NOT_SHIPPED"
              >
                <div>
                  <account-order-details-shipment-grid
                    :base-properties="baseProperties"
                    :order-data="orderData"
                    :table-headers="tableHeaders"
                    :table-data="orderData[tab.key].NOT_SHIPPED"
                    shipment="NOT_SHIPPED"
                  />
                </div>
              </account-order-details-shipment>
            </collapse-item>

            <template v-for="section in sections">
              <collapse-item
                v-if="isAllocatedOrBackordered(tab.key, section.key)"
                :key="section.key"
                :name="section.label"
              >
                <div
                  v-if="section.key == 'BACKORDERED'"
                  slot="title"
                  class="heading heading--sm"
                  v-html="`${section.label} (${orderData.subTotals.BACKORDERED.quantity})`"
                />
                <div
                  v-if="section.key == 'ALLOCATED'"
                  slot="title"
                  class="heading heading--sm"
                  v-html="`${section.label} (${orderData.subTotals.ALLOCATED.quantity})`"
                />
                <account-order-details-backordered-grid
                  :order-data="orderData"
                  :base-properties="baseProperties"
                  :headers="tableHeaders"
                  :items="sectionItems(tab.key, section.key)"
                  :shipment-info="orderData[tab.key][section.key]"
                  :section-key="section.key"
                  :page-size="50"
                />
              </collapse-item>
            </template>
          </collapse>
        </tab-pane>
      </template>
    </tabs>

    <section-header v-if="!consumerFriendlyMode" size="md" variants="dividing">
      <heading level="1" size="md" v-html="bp('text.ordersummary')" />
    </section-header>

    <div v-if="!consumerFriendlyMode" class="grid-x grid-margin-x">
      <div class="cell medium-6 large-4 large-offset-8">
        <account-order-details-summary v-if="!loading" :order-data="orderData" :base-properties="baseProperties" />
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import AccountOrderDetailsInfo from '@/components/my-account/AccountOrderDetailsInfo';
import AccountOrderDetailsSummary from '@/components/my-account/AccountOrderDetailsSummary';
import AccountOrderDetailsShipmentGrid from '@/components/my-account/AccountOrderDetailsShipmentGrid';
import AccountOrderDetailsBackorderedGrid from '@/components/my-account/AccountOrderDetailsBackorderedGrid';
import AccountOrderDetailsShipmentInfo from '@/components/my-account/AccountOrderDetailsShipmentInfo';
import LoadOrderDetails from '@/components/containers/my-account/mixins/load-order-details';
import findIndex from 'lodash/findIndex';

export default {
  components: {
    SectionHeader,
    TrekButton,
    Heading,
    AccountOrderDetailsInfo,
    AccountOrderDetailsSummary,
    AccountOrderDetailsShipmentGrid,
    AccountOrderDetailsBackorderedGrid,
    AccountOrderDetailsShipmentInfo,
  },
  mixins: [LoadOrderDetails],
  props: {
    textBikes: {
      type: String,
      default: '',
    },
    textAccessories: {
      type: String,
      default: '',
    },
    textNonStock: {
      type: String,
      default: '',
    },
    textAllocatedItems: {
      type: String,
      default: '',
    },
    textBackorderedItems: {
      type: String,
      default: '',
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      orderUrl: this.getUrl(),
    };
  },
  provide() {
    return {
      container: this,
    };
  },

  computed: {
    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },
  async mounted() {
    await this.loadData(this.orderUrl);
  },

  methods: {
    sectionItems(productType, sectionKey) {
      return this.orderData[productType][sectionKey];
    },
    getUrl() {
      const pathArray = window.location.pathname.split('/');
      const index = findIndex(pathArray, (x) => x == 'order');
      let orderId = '';

      if (index > -1 && pathArray.length > index + 1) {
        orderId = pathArray[index + 1];
      }

      return `/account/order/json/v2/${orderId}${window.location.search}`;
    },
    downloadOrderDetails() {
      window.location.href = `${this.encodedContextPath}/my-account/order/export/${this.orderData.code}?company=${this.orderData.details.orderCompany}&ordertype=${this.orderData.details.orderType}`;
    },
    isAllocatedOrBackordered(tabKey, sectionKey) {
      return this.orderData[tabKey][sectionKey] && (sectionKey == 'BACKORDERED' || sectionKey == 'ALLOCATED');
    },
  },
};
</script>
